import { Button } from 'shared/components/ui';
import React, { useState, useRef, useEffect, useId } from 'react';
import { useNavigate } from 'react-router-dom';
import { Notifications, NotificationsNone, Close, DoneAll } from '@mui/icons-material';
import { useNotifications } from '../../features/turnos/hooks/useNotifications';
import TurnoNotification, { getTurnoNotification } from './TurnoNotification';
import './NotificationBell.css';

const FILTROS = [
  { key: 'todas', label: 'Todas' },
  { key: 'no-leidas', label: 'No leídas' },
  { key: 'leidas', label: 'Leídas' }
];

// Categorías para separar gestión de turnos de liquidación/facturación.
const CATEGORIAS = [
  { key: 'todas', label: 'Todas' },
  { key: 'turnos', label: 'Turnos' },
  { key: 'facturacion', label: 'Facturación' }
];

const NotificationBell = () => {
  const { notifications, count, loading, filter, setFilter, markAsRead, markAllAsRead } = useNotifications();
  const [isOpen, setIsOpen] = useState(false);
  const [categoria, setCategoria] = useState('todas');
  const containerRef = useRef(null);
  const buttonRef = useRef(null);
  const panelId = useId();
  const navigate = useNavigate();

  // Cerrar con click exterior o Escape
  useEffect(() => {
    if (!isOpen) return;

    const handleClickOutside = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };

    const handleEscape = (e) => {
      if (e.key === 'Escape') {
        setIsOpen(false);
        buttonRef.current?.focus();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEscape);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isOpen]);

  const handleNotificationClick = (notification) => {
    const link = notification.link || (notification.idTurno ? `/grilla-datos-practicas?turno=${notification.idTurno}` : null);
    if (link) {
      navigate(link);
      setIsOpen(false);
    }
  };

  // Filtro por categoría aplicado sobre la lista ya filtrada por leídas/no leídas.
  // Las notificaciones sin categoría (compatibilidad) se tratan como "turnos".
  const notificacionesVisibles = categoria === 'todas'
    ? notifications
    : notifications.filter(n => (n.categoria || 'turnos') === categoria);

  return (
    <div className="notification-bell" ref={containerRef}>
      <Button
        type="button"
        ref={buttonRef}
        className="notification-btn"
        aria-label="Notificaciones"
        aria-expanded={isOpen}
        aria-controls={isOpen ? panelId : undefined}
        onClick={() => setIsOpen(prev => !prev)}
      >
        <Notifications sx={{ fontSize: 20 }} />
        {count > 0 && <span className="notification-badge">{count > 99 ? '99+' : count}</span>}
      </Button>
      
      {isOpen && (
        <section className="notification-dropdown" id={panelId} aria-label="Panel de notificaciones">
          <div className="notification-header">
            <span className="notification-header-icon" aria-hidden="true"><NotificationsNone /></span>
            <div className="notification-heading">
              <h3>Notificaciones</h3>
              <p>{count > 0 ? `${count} sin leer` : 'Estás al día'}</p>
            </div>
            <button type="button" className="notification-close" aria-label="Cerrar notificaciones" onClick={() => { setIsOpen(false); buttonRef.current?.focus(); }}>
              <Close fontSize="small" />
            </button>
          </div>
          <div className="notification-filters" role="group" aria-label="Estado de lectura">
            {FILTROS.map(f => (
              <button
                key={f.key}
                type="button"
                aria-pressed={filter === f.key}
                className={`notification-filter-btn ${filter === f.key ? 'active' : ''}`}
                onClick={() => setFilter(f.key)}
              >
                {f.label}
              </button>
            ))}
          </div>
          <div className="notification-categorias" role="group" aria-label="Categorías de notificaciones">
            {CATEGORIAS.map(c => (
              <button
                key={c.key}
                type="button"
                className={`notification-category-btn ${categoria === c.key ? 'active' : ''}`}
                aria-pressed={categoria === c.key}
                onClick={() => setCategoria(c.key)}
              >
                {c.key === 'todas' ? 'Todo' : c.label}
              </button>
            ))}
          </div>
          {count > 0 && (
            <div className="notification-toolbar">
              <button type="button" className="notification-mark-all" onClick={markAllAsRead}>
                <DoneAll fontSize="small" /> Marcar todas como leídas
              </button>
            </div>
          )}
          <div className="notification-list" aria-busy={loading}>
            {notificacionesVisibles.length > 0 ? (
              notificacionesVisibles.map((notification, index) => {
                const turno = getTurnoNotification(notification);
                return (
                <div
                  key={notification.idNotificacion || index}
                  className={`notification-item ${notification.lectura ? 'notification-read' : ''}`}
                >
                  <div className="notification-dot" aria-label={notification.lectura ? 'Leída' : 'Sin leer'}></div>
                  <div className="notification-content">
                    {turno ? <TurnoNotification turno={turno} /> : (
                      <>
                        {notification.envia && (
                          <span className="notification-from">{notification.envia === 'SUGI_SYSTEM' ? 'Aviso automático' : `De: ${notification.envia}`}</span>
                        )}
                        <span className="notification-category-label">{notification.categoria === 'facturacion' ? 'Facturación' : 'Turnos'}</span>
                        {(notification.link || notification.idTurno) ? (
                      <button type="button" className="notification-message-link" onClick={() => handleNotificationClick(notification)}>
                        {notification.mensaje}
                      </button>
                        ) : <p>{notification.mensaje}</p>}
                      </>
                    )}
                    <div className="notification-actions">
                      {(notification.link || notification.idTurno) && (
                        <Button
                          type="button"
                          className="notification-access-btn"
                          aria-label={notification.idTurno ? 'Acceder al turno' : 'Acceder'}
                          onClick={() => handleNotificationClick(notification)}
                        >
                          {notification.idTurno ? 'Acceder al turno' : 'Acceder'}
                        </Button>
                      )}
                      {!notification.lectura && notification.idNotificacion && (
                        <Button
                          type="button"
                          className="notification-mark-btn"
                          onClick={() => markAsRead(notification.idNotificacion)}
                        >
                          Marcar como leída
                        </Button>
                      )}
                    </div>
                  </div>
                </div>
                );
              })
            ) : (
              <div className="notification-empty" role="status">
                <span className="notification-empty-icon" aria-hidden="true"><NotificationsNone sx={{ fontSize: 28 }} /></span>
                <p>{
                  loading ? 'Cargando notificaciones…'
                  : categoria === 'turnos' ? 'No hay notificaciones de turnos'
                  : categoria === 'facturacion' ? 'No hay notificaciones de facturación'
                  : filter === 'no-leidas' ? 'No hay notificaciones sin leer'
                  : filter === 'leidas' ? 'No hay notificaciones leídas'
                  : 'No hay notificaciones'
                }</p>
                <span>{loading ? 'Un momento, por favor.' : filter !== 'todas' || categoria !== 'todas' ? 'Prueba con otro filtro para ver más actividad.' : 'Las novedades de turnos y facturación aparecerán aquí.'}</span>
              </div>
            )}
          </div>
        </section>
      )}
    </div>
  );
};

export default NotificationBell;
